<template>
  <div class="pull-logs-container">
    <!-- Header Section with Date Picker -->
    <b-row class="mb-4 align-items-end">
      <b-col md="4" lg="3" sm="12">
        <b-form-group
          label="Select Date for Syncing"
          label-class="font-weight-bold text-dark"
          class="mb-0"
        >
          <b-form-datepicker
            v-model="selectedDate"
            placeholder="Choose a date"
            :date-format-options="{
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }"
            reset-button
            today-button
            class="simple-datepicker"
            @input="logSelectedDate"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Data Table -->
    <DataTable
      v-if="items"
      :total="total"
      :allow-search="true"
      :filter="filter"
      :loading="load"
      @Refresh="refresh"
    >
      <template #customTable>
        <b-table
          :sort-desc.sync="filter.sort_direction"
          :items="items"
          responsive
          striped
          hover
          :fields="fields"
          primary-key="id"
          show-empty
          :empty-text="$t('Global.empty_text')"
          class="mb-0"
        >
          <!-- Status Column with Enhanced Badges -->
          <template #cell(status)="data">
            <div v-if="hasPermission('edit_branches')">
              <b-badge
                pill
                :variant="getEnhancedStatusVariant(data.item)"
                class="status-badge"
                v-b-tooltip.hover
                :title="getStatusTooltip(data.item)"
              >
                <b-icon :icon="getStatusIcon(data.item)" class="mr-1" />
                {{ getEnhancedStatusText(data.item) }}
              </b-badge>
            </div>
          </template>

          <!-- Cases No Column -->
          <template #cell(cases_no)="data">
            {{
              getEffectiveStatus(data.item) === "success" &&
              data.item.cases_no != null
                ? data.item.cases_no
                : "—"
            }}
          </template>

          <!-- Success Cases No Column -->
          <template #cell(success_cases_no)="data">
            <span
              v-if="getEffectiveStatus(data.item) === 'success'"
              class="text-success font-weight-bold"
            >
              {{
                data.item.success_cases_no != null
                  ? data.item.success_cases_no
                  : "—"
              }}
            </span>
            <span v-else>—</span>
          </template>

          <!-- Fail Cases No Column -->
          <template #cell(fail_cases_no)="data">
            <span
              v-if="getEffectiveStatus(data.item) === 'success'"
              class="text-danger font-weight-bold"
            >
              {{
                data.item.fail_cases_no != null ? data.item.fail_cases_no : "—"
              }}
            </span>
            <span v-else>—</span>
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <div
              v-if="hasPermission('edit_branches')"
              class="d-flex align-items-center"
            >
              <!-- Log the values for debugging -->
              <div
                v-if="debugPullButton(data.item)"
                style="display: none"
              ></div>

              <!-- Loading State -->
              <div
                v-if="
                  $store.state.pullLogs.loadPull &&
                  $store.state.pullLogs.conec_name == data.item.connection
                "
                class="text-muted"
              >
                <b-spinner small class="mr-1" />
                Initializing...
              </div>

              <!-- Pulling in Progress with Live Logs -->
              <div
                v-else-if="pullStatusForItem(data.item.connection).is_pulling"
                :class="{'progress-container-enhanced': pullStatusForItem(data.item.connection).status === 'processing'}"
              >
                <div :class="['d-flex', 'align-items-center', {'mb-1': pullStatusForItem(data.item.connection).status === 'processing'}]">
                  <!-- Processing State -->
                  <template v-if="pullStatusForItem(data.item.connection).status === 'processing'">
                    <b-progress
                      :value="pullProgress[data.item.connection] || 0"
                      max="100"
                      show-progress
                      animated
                      :variant="getProgressVariant(data.item.connection)"
                      class="flex-grow-1 mr-2"
                      height="1.2rem"
                    />
                    <small class="text-muted mr-2 font-weight-bold">
                      {{ pullProgress[data.item.connection] || 0 }}%
                      <span
                        v-if="getProgressLabel(data.item.connection)"
                        class="ml-1 text-info"
                      >
                        ({{ getProgressLabel(data.item.connection) }})
                      </span>
                    </small>
                  </template>

                  <!-- Pending State -->
                  <template v-else-if="pullStatusForItem(data.item.connection).status === 'pending'">
                    <div class="text-warning font-weight-bold d-flex align-items-center">
                      <b-icon icon="hourglass-split" animation="fade" ></b-icon>
                      Queued...
                    </div>
                  </template>

                </div>
                <!-- Live Log Message -->
                <div
                  v-if="getLatestLogMessage(data.item.connection)"
                  class="live-log-message"
                >
                  <small class="text-muted">
                    <b-icon icon="info-circle" class="mr-1" />
                    {{ getLatestLogMessage(data.item.connection) }}
                  </small>
                </div>
                <!-- Estimated Time -->
                <div
                  v-if="getEstimatedTime(data.item.connection)"
                  class="estimated-time"
                >
                  <small class="text-info">
                    <b-icon icon="clock" class="mr-1" />
                    Est. {{ getEstimatedTime(data.item.connection) }}
                  </small>
                </div>
              </div>

              <!-- Pull Button - Show for success/failed, hide for pending/processing -->
              <b-button
                v-else
                pill
                :variant="isPullButtonEnabled(data.item) ? 'info' : 'secondary'"
                size="sm"
                :disabled="!isPullButtonEnabled(data.item)"
                :class="{ 'disabled-cursor': !isPullButtonEnabled(data.item) }"
                @click="pullDataWithDate(data.item.connection)"
                v-b-tooltip.hover
                :title="getPullButtonTooltip(data.item)"
              >
                <b-icon icon="cloud-download" class="mr-1" />
                Pull
              </b-button>
              <!-- Details Icon — item.id هو دايماً الـ pull_log_id -->
              <b-button
                variant="link"
                size="sm"
                class="ml-2"
                @click="showLogsModal(data.item.connection, data.item.id)"
                v-b-tooltip.hover
                title="View pull details"
              >
                <b-icon icon="clipboard-data" />
              </b-button>
            </div>
          </template>


        </b-table>
      </template>
    </DataTable>

    <!-- Fallback Message if Data Fails to Load -->
    <div v-else class="text-center text-muted py-4">
      <b-spinner small class="mr-2" />
      Loading data... If this takes too long, please refresh the page.
    </div>

    <!-- Logs Modal -->
    <!-- Details Modal -->
    <b-modal
      v-model="showModal"
      :title="
        pullLogSummary
          ? 'Pull Log \u2014 ' +
            (pullLogSummary.branch_name || currentConnection)
          : 'Pull Logs'
      "
      ok-only
      ok-title="Close"
      size="lg"
      @hidden="stopListeningToLogs"
      centered
      scrollable
    >
      <!-- Pull Summary Card -->
      <div v-if="pullLogSummary" class="pull-summary-wrapper mb-4">
        <b-row>
          <!-- Status -->
          <b-col cols="6" md="3" class="mb-3 mb-md-0">
            <div class="summary-stat-box">
              <div class="summary-label">Status</div>
              <div class="summary-value mt-2">
                <b-badge
                  pill
                  :variant="
                    pullLogSummary.status === 'success'
                      ? 'success'
                      : pullLogSummary.status === 'processing'
                      ? 'primary'
                      : pullLogSummary.status === 'pending'
                      ? 'warning'
                      : 'danger'
                  "
                  class="px-1 py-1 text-capitalize shadow-sm"
                >
                  {{ pullLogSummary.status }}
                </b-badge>
              </div>
            </div>
          </b-col>

          <!-- Progress -->
          <b-col cols="6" md="3" class="mb-3 mb-md-0">
            <div class="summary-stat-box">
              <div class="summary-label">Progress</div>
              <div class="summary-value mt-2 w-100">
                <b-progress
                  v-if="pullLogSummary.status === 'processing' || pullLogSummary.status === 'success'"
                  :value="pullLogSummary.progress || 0"
                  max="100"
                  show-progress
                  height="1.25rem"
                  :variant="
                    pullLogSummary.status === 'success' ? 'success' : 'primary'
                  "
                  class="w-100 shadow-sm"
                />
                <span v-else-if="pullLogSummary.status === 'pending'" class="text-warning font-weight-bold">
                  <b-icon icon="hourglass-split" class="mr-1"></b-icon> Queued
                </span>
                <span v-else class="text-muted font-weight-bold">—</span>
              </div>
            </div>
          </b-col>

          <!-- Total Cases -->
          <b-col cols="6" md="3" class="mb-3 mb-md-0">
            <div class="summary-stat-box">
              <div class="summary-label">Total Cases</div>
              <div class="summary-value mt-2">
                <span class="stat-number text-dark">{{
                  pullLogSummary.status === "success" &&
                  pullLogSummary.cases_no != null
                    ? pullLogSummary.cases_no
                    : "—"
                }}</span>
              </div>
            </div>
          </b-col>

          <!-- Success / Failed -->
          <b-col cols="6" md="3" class="mb-3 mb-md-0">
            <div class="summary-stat-box">
              <div class="summary-label">Success / Failed</div>
              <div class="summary-value mt-2 d-flex align-items-center">
                <span class="stat-number text-success">{{
                  pullLogSummary.status === "success" &&
                  pullLogSummary.success_cases_no != null
                    ? pullLogSummary.success_cases_no
                    : "—"
                }}</span>
                <span class="mx-2 text-muted" style="font-size: 1.25rem;">/</span>
                <span class="stat-number text-danger">{{
                  pullLogSummary.status === "success" &&
                  pullLogSummary.fail_cases_no != null
                    ? pullLogSummary.fail_cases_no
                    : "—"
                }}</span>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col cols="6">
            <div class="summary-stat-box bg-light-subtle">
              <div class="summary-label">Started At</div>
              <div class="summary-value mt-1 text-secondary font-weight-bold" style="font-size: 0.85rem;">
                <b-icon icon="play-circle" class="mr-1 opacity-50"></b-icon>
                {{ pullLogSummary.start_date || "—" }}
              </div>
            </div>
          </b-col>
          <b-col cols="6">
            <div class="summary-stat-box bg-light-subtle">
              <div class="summary-label">Ended At</div>
              <div class="summary-value mt-1 text-secondary font-weight-bold" style="font-size: 0.85rem;">
                <b-icon icon="stop-circle" class="mr-1 opacity-50"></b-icon>
                {{ pullLogSummary.end_date || "—" }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Log Messages Timeline -->
      <div class="log-container-wrapper mb-2">
        <div class="log-container-header">
          <b-icon icon="terminal" class="mr-2"></b-icon> System Logs
        </div>
        <div class="log-container">
          <div v-if="isLogsLoading" class="text-center text-muted py-4">
            <b-spinner small class="mr-2" />
            Loading logs...
          </div>
          <div
            v-else-if="pullLogDetails.length === 0"
            class="text-center text-muted py-4"
          >
            <span v-if="pullLogSummary && pullLogSummary.status === 'pending'">
              <b-icon icon="hourglass-split" class="mr-2 mb-2" scale="1.5"></b-icon><br/>
              Pull request is queued and waiting to start...
            </span>
            <span v-else>
              No logs available yet.
            </span>
          </div>
          <div
            v-for="log in pullLogDetails"
            :key="log.id || log.timestamp"
            :class="`log-${log.level}`"
            class="log-entry"
          >
            <span class="log-timestamp">[{{ formatTimestamp(log.created_at) }}]</span>
            <b-icon :icon="getLogIcon(log.level)" class="mx-2" />
            <span class="log-message">{{ log.message }}</span>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BFormGroup,
  BFormDatepicker,
  BModal,
  BProgress,
  BRow,
  BCol,
  BSpinner,
  BIcon,
  BOverlay,
} from "bootstrap-vue";
import { mapGetters, mapActions } from "vuex";
import DataTable from "@/views/components/table/DataTable";

export default {
  components: {
    DataTable,
    BTable,
    BButton,
    BFormGroup,
    BFormDatepicker,
    BModal,
    BProgress,
    BRow,
    BCol,
    BSpinner,
    BIcon,
    BOverlay,
  },
  data() {
    return {
      filter: { removed: 0, sort_direction: false },
      selectedDate: localStorage.getItem("pullLogs_selectedDate") || null,
      polling: null,
      showModal: false,
      currentConnection: null,
      isLoading: false,
      isLogsLoading: false,
      logsPolling: null,
      activePollingConnections: new Set(),
      pullHistory: {}, // Track pull_id for each connection
      notificationPermission: false,
      soundEnabled: true, // Can be toggled by user
    };
  },
  props: {
    logs: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      items: "pullLogs/items",
      total: "pullLogs/total",
      load: "pullLogs/load",
      loadPull: "pullLogs/loadPull",
      conec_name: "pullLogs/conec_name",
      pullStatus: "pullLogs/pullStatus",
      pullLogDetails: "pullLogs/pullLogDetails",
      pullLogSummary: "pullLogs/pullLogSummary",
      pullProgress: "pullLogs/pullProgress",
      pullIds: "pullLogs/pullIds",
    }),
    fields() {
      let fields = [
        {
          key: "branch_id",
          label: "Branch Code",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "branch_name",
          label: "Branch Name",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "status",
          label: "Status",
          thClass: "align-middle text-center",
          tdClass: "align-middle text-center",
        },
        {
          key: "cases_no",
          label: "Number of Cases",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "success_cases_no",
          label: "Auto Checks: Success",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "fail_cases_no",
          label: "Auto Checks: Failed",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "end_date",
          label: "Last Sync",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "start_date",
          label: "Sync Started",
          sortable: true,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "Actions",
          label: "Actions",
          thClass: "align-middle text-center",
          tdClass: "align-middle text-center",
        },
      ];
      if (this.logs) {
        fields = [
          {
            key: "name",
            label: "Branch Name",
            sortable: true,
            thClass: "align-middle",
            tdClass: "align-middle",
          },
        ];
      }
      return fields;
    },
  },
  methods: {
    ...mapActions("pullLogs", [
      "pullLogs",
      "pullData",
      "fetchPullStatus",
      "fetchPullLogs",
    ]),
    async refresh(query) {
      try {
        this.isLoading = true;
        query = { ...query, type: this.type };
        await this.$store.dispatch("pullLogs/pullLogs", { query });
      } catch (error) {
        console.error("Error refreshing data:", error);
        this.$bvToast.toast("Failed to load data. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    refreshWithDate() {
      if (!this.selectedDate) {
        this.$bvToast.toast("Please select a date to refresh the data.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 3000,
        });
        return;
      }
      this.refresh({ date: this.selectedDate });
    },
    async pullDataWithDate(connection) {
      if (!this.selectedDate) {
        this.$bvToast.toast("Please select a date before starting the pull.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 3000,
        });
        return;
      }
      try {
        this.isLoading = true;
        const data = await this.$store.dispatch("pullLogs/pullData", {
          id: connection,
          date: this.selectedDate,
        });

        // Track pull_id for history
        if (data && data.pull_id) {
          this.pullHistory[connection] = data.pull_id;
        }

        // Add to active polling connections
        this.activePollingConnections.add(connection);

        // Start polling if not already running
        if (!this.polling) {
          this.startPolling();
        }

        // Show appropriate message
        if (data.already_in_progress) {
          this.$bvToast.toast(
            `Pull is already in progress for ${connection}. Progress: ${
              data.progress || 0
            }%`,
            {
              title: "Already Running",
              variant: "warning",
              solid: true,
              toaster: "b-toaster-top-center",
              autoHideDelay: 5000,
            },
          );
        } else {
          this.$bvToast.toast("Pull queued successfully.", {
            title: "Success",
            variant: "success",
            solid: true,
            toaster: "b-toaster-top-center",
            autoHideDelay: 3000,
          });
        }
      } catch (error) {
        console.error("Error starting pull:", error);

        const errorStatus = error.response?.status;
        const errorData = error.response?.data;
        const errorMessage =
          errorData?.error || errorData?.message || errorData?.details;

        if (errorStatus === 503) {
          // Oracle connection error — الـ Record مش اتغيرش، بس نعرض رسالة
          this.$bvToast.toast(
            errorMessage || "Cannot connect to Oracle. Please contact DB team.",
            {
              title: "Oracle Connection Error",
              variant: "danger",
              solid: true,
              toaster: "b-toaster-top-center",
              autoHideDelay: 8000,
            },
          );
          await this.refresh({});
        } else if (errorStatus === 422) {
          // Validation error — اسم Connection غلط أو date مش موجودة
          const validationMsg =
            errorData?.errors?.date?.[0] ||
            errorMessage ||
            "Validation error. Please check the date and try again.";
          this.$bvToast.toast(validationMsg, {
            title: "Validation Error",
            variant: "warning",
            solid: true,
            toaster: "b-toaster-top-center",
            autoHideDelay: 6000,
          });
        } else {
          this.$bvToast.toast(
            errorMessage || "Failed to start pull. Please try again.",
            {
              title: "Error",
              variant: "danger",
              solid: true,
              toaster: "b-toaster-top-center",
              autoHideDelay: 5000,
            },
          );
        }
      } finally {
        this.isLoading = false;
      }
    },
    startPolling() {
      this.polling = setInterval(async () => {
        await this.pollActiveConnections();
      }, 3000); // Poll every 3 seconds
    },
    async pollActiveConnections() {
      const connectionsToRemove = [];

      for (const connection of this.activePollingConnections) {
        try {
          const data = await this.$store.dispatch(
            "pullLogs/fetchPullStatus",
            connection,
          );

          // Check if completed
          if (data.status === "success" || data.status === "failed") {
            connectionsToRemove.push(connection);

            // Show desktop notification
            this.showDesktopNotification(connection, data.status);

            // Play sound alert
            if (this.soundEnabled) {
              this.playCompletionSound(data.status);
            }

            // Show toast notification
            this.$bvToast.toast(
              data.status === "success"
                ? `Pull completed successfully for ${
                    data.branch_name || connection
                  }`
                : `Pull failed for ${data.branch_name || connection}`,
              {
                title: data.status === "success" ? "Success ✅" : "Failed ❌",
                variant: data.status === "success" ? "success" : "danger",
                solid: true,
                toaster: "b-toaster-top-center",
                autoHideDelay: 5000,
              },
            );

            // Refresh table data
            await this.refresh({});
          }
        } catch (error) {
          console.error(`Error fetching pull status for ${connection}:`, error);
        }
      }

      // Remove completed connections
      connectionsToRemove.forEach((conn) =>
        this.activePollingConnections.delete(conn),
      );

      // Stop polling if no active connections
      if (this.activePollingConnections.size === 0) {
        this.stopPolling();
      }
    },
    stopPolling() {
      if (this.polling) {
        clearInterval(this.polling);
        this.polling = null;
      }
    },
    /**
     * Auto-resume polling for items that are still pulling after page refresh.
     * Immediately sets pullStatus in Vuex so the progress bar shows right away.
     */
    async resumeActivePolling() {
      if (!this.items || this.items.length === 0) return;

      const statusFetches = [];

      for (const item of this.items) {
        const status = item.status;
        if (status === "processing" || status === "pending") {
          this.activePollingConnections.add(item.connection);

          // إظهار الـ Progress Bar فوراً من بيانات الـ List
          this.$store.commit("pullLogs/SET_PULL_STATUS", {
            [item.connection]: { status: status, is_pulling: true },
          });
          this.$store.commit("pullLogs/SET_PULL_PROGRESS", {
            [item.connection]: item.progress || 0,
          });

          // جيب الـ Status الحقيقي + pull_id من الـ Backend بشكل متوازي
          statusFetches.push(
            this.$store
              .dispatch("pullLogs/fetchPullStatus", item.connection)
              .catch((err) =>
                console.error("Error fetching initial status:", err),
              ),
          );
        }
      }

      await Promise.allSettled(statusFetches);

      if (this.activePollingConnections.size > 0) {
        this.startPolling();
      }
    },
    /**
     * @param {string} connection - اسم الـ connection
     * @param {number} itemId - item.id من الـ List (= pull_log_id دايماً)
     */
    async showLogsModal(connection, itemId) {
      this.currentConnection = connection;
      this.showModal = true;
      this.isLogsLoading = true;
      this.$store.commit("pullLogs/SET_PULL_LOG_DETAILS", []);
      this.$store.commit("pullLogs/SET_PULL_LOG_SUMMARY", null);

      const pullId = this.pullIds[connection] || itemId;

      if (!pullId) {
        this.$bvToast.toast("No pull log ID found for this row.", {
          title: "Warning",
          variant: "warning",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 3000,
        });
        return;
      }
      try {
        await this.$store.dispatch("pullLogs/fetchPullLogs", pullId);
        this.isLogsLoading = false;

        if (
          this.pullLogSummary &&
          (this.pullLogSummary.status === "pending" ||
            this.pullLogSummary.status === "processing")
        ) {
          this.startListeningToLogs(pullId);
        }
      } catch (error) {
        this.isLogsLoading = false;
        console.error("Error fetching logs:", error);
        this.$bvToast.toast("Failed to load logs. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-top-center",
          autoHideDelay: 5000,
        });
      }
    },
    startListeningToLogs(pullId) {
      if (!pullId) return;
      this.logsPolling = setInterval(async () => {
        try {
          await this.$store.dispatch("pullLogs/fetchPullLogs", pullId);
          if (
            this.pullLogSummary &&
            this.pullLogSummary.status !== "pending" &&
            this.pullLogSummary.status !== "processing"
          ) {
            if (this.logsPolling) {
              clearInterval(this.logsPolling);
              this.logsPolling = null;
            }
          }

          this.$nextTick(() => {
            const logContainer = this.$el.querySelector(".log-container");
            if (logContainer) {
              logContainer.scrollTop = logContainer.scrollHeight;
            }
          });
        } catch (error) {
          console.error("Error polling logs:", error);
        }
      }, 3000); // Poll logs every 3 seconds
    },
    stopListeningToLogs() {
      if (this.logsPolling) {
        clearInterval(this.logsPolling);
        this.logsPolling = null;
      }
      this.currentConnection = null;
      this.showModal = false;
    },
    isValidDate(dateString) {
      const date = new Date(dateString);
      return !isNaN(date.getTime());
    },
    logSelectedDate() {
      // Persist selected date to localStorage
      if (this.selectedDate) {
        localStorage.setItem("pullLogs_selectedDate", this.selectedDate);
      } else {
        localStorage.removeItem("pullLogs_selectedDate");
      }
    },
    debugPullButton(item) {
      console.log("Debugging Pull Button for item:", item);
      console.log("selectedDate:", this.selectedDate);
      console.log("updated_at:", item.updated_at);
      console.log("end_date:", item.end_date);
      console.log(
        "Is updated_at recent?",
        this.isValidDate(item.updated_at) &&
          new Date(item.updated_at) >
            new Date(new Date().setDate(new Date().getDate() - 1)),
      );
      console.log(
        "Is Pull button disabled?",
        !this.selectedDate ||
          (this.isValidDate(item.updated_at) &&
            new Date(item.updated_at) >
              new Date(new Date().setDate(new Date().getDate() - 1)) &&
            item.cases_no !== null) ||
          item.end_date,
      );
      return true;
    },

    pullStatusForItem(connection) {
      return this.pullStatus[connection] || {};
    },

    /**
     * Check if a date string is today's date
     */
    isToday(dateString) {
      if (!dateString) return false;
      const date = new Date(dateString);
      const today = new Date();
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      );
    },

    /**
     * Get the effective status for an item.
     * Priority: in-memory pullStatus (from active polling) > item.status (from backend list)
     */
    getEffectiveStatus(item) {
      // 1st priority: live status from active polling
      const liveStatus = this.pullStatus[item.connection]?.status;
      if (liveStatus) return liveStatus;
      // 2nd priority: item.status from backend list response
      if (item.status) return item.status;
      // 3rd fallback: check end_date
      if (this.isToday(item.end_date)) return "success";
      return "failed";
    },

    /**
     * Get computed status based on end_date (legacy fallback)
     */
    getComputedStatus(item) {
      return this.getEffectiveStatus(item);
    },

    /**
     * Get status button variant based on computed status
     */
    getStatusVariant(item) {
      const status = this.getComputedStatus(item);
      return status === "success" ? "success" : "danger";
    },

    /**
     * Get status button text based on computed status
     */
    getStatusText(item) {
      const status = this.getComputedStatus(item);
      return status === "success" ? "Success" : "Failed";
    },

    /**
     * Get status button tooltip based on computed status
     */
    getStatusTooltip(item) {
      const status = this.getComputedStatus(item);
      return status === "success"
        ? "Last sync successful (synced today)"
        : "Sync required (last sync is outdated)";
    },

    /**
     * Check if pull button should be enabled
     * Enabled when:
     * - A date is selected AND
     * - Status is failed (end_date is not today)
     */
    isPullButtonEnabled(item) {
      if (!this.selectedDate) return false;
      // Disable if pending or processing
      const liveStatus = this.pullStatus[item.connection]?.status;
      if (liveStatus === "pending" || liveStatus === "processing") return false;
      // Enable for success (re-pull) and failed
      return true;
    },

    /**
     * Get pull button tooltip
     */
    getPullButtonTooltip(item) {
      if (!this.selectedDate) {
        return "Please select a date first";
      }
      const liveStatus = this.pullStatus[item.connection]?.status;
      if (liveStatus === "pending" || liveStatus === "processing") {
        return "Pull is already in progress";
      }
      return "Start pulling data for this branch";
    },

    /**
     * Get progress label based on progress percentage
     */
    getProgressLabel(connection) {
      const progress = this.pullProgress[connection] || 0;
      if (progress <= 10) return "Connecting...";
      if (progress <= 15) return "Starting pull";
      if (progress <= 80) return "Pulling data";
      if (progress <= 90) return "Gathering stats";
      if (progress <= 99) return "AutoCheck & Assign";
      return "Completing";
    },

    // UX Enhancement Methods
    getEnhancedStatusVariant(item) {
      const status = this.getEffectiveStatus(item);
      if (status === "pending") return "warning";
      if (status === "processing") return "primary";
      if (status === "success") return "success";
      if (status === "failed") return "danger";
      return "secondary";
    },

    getEnhancedStatusText(item) {
      const status = this.getEffectiveStatus(item);
      if (status === "pending") return "Pending";
      if (status === "processing") return "Pulling...";
      if (status === "success") return "Success";
      if (status === "failed") return "Failed";
      return status || "Unknown";
    },

    getStatusIcon(item) {
      const status = this.getEffectiveStatus(item);
      if (status === "pending") return "hourglass-split";
      if (status === "processing") return "arrow-repeat";
      if (status === "success") return "check-circle";
      if (status === "failed") return "x-circle";
      return "question-circle";
    },

    getProgressVariant(connection) {
      const status = this.pullStatus[connection]?.status;
      if (status === "pending") return "warning";
      if (status === "processing") return "primary";
      return "success";
    },

    getLatestLogMessage(connection) {
      if (!this.pullLogDetails || this.pullLogDetails.length === 0) return null;
      const latestLog = this.pullLogDetails[this.pullLogDetails.length - 1];
      return latestLog?.message || null;
    },

    getEstimatedTime(connection) {
      // Backend should provide this in the status response
      const status = this.pullStatus[connection];
      if (status && status.estimated_time) {
        return status.estimated_time;
      }
      return null;
    },

    getLogIcon(level) {
      if (level === "error") return "exclamation-triangle";
      if (level === "warning") return "exclamation-circle";
      if (level === "success") return "check-circle";
      return "info-circle";
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    },



    async requestNotificationPermission() {
      if ("Notification" in window && Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        this.notificationPermission = permission === "granted";
      } else if (Notification.permission === "granted") {
        this.notificationPermission = true;
      }
    },

    showDesktopNotification(connection, status) {
      if (!this.notificationPermission || !("Notification" in window)) return;

      const title = status === "success" ? "Pull Completed" : "Pull Failed";
      const body = `Data pull for ${connection} ${
        status === "success" ? "completed successfully" : "failed"
      }.`;
      const icon =
        status === "success" ? "/success-icon.png" : "/error-icon.png";

      new Notification(title, { body, icon });
    },

    playCompletionSound(status) {
      try {
        const audio = new Audio(
          status === "success" ? "/sounds/success.mp3" : "/sounds/error.mp3",
        );
        audio.volume = 0.3;
        audio.play().catch((err) => console.log("Audio play failed:", err));
      } catch (error) {
        console.log("Sound alert not available:", error);
      }
    },
  },
  async mounted() {
    await this.refresh({});
    this.requestNotificationPermission();
    // Auto-resume polling for items that are still pulling
    this.resumeActivePolling();
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopListeningToLogs();
  },
};
</script>

<style scoped>
.pull-logs-container {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  min-height: 100%;
}

/* Simple Date Picker Styling */
.simple-datepicker {
  border-radius: 6px;
}

/* Table Styling */
.b-table {
  background-color: #fff;
  border-radius: 8px;
  font-size: 14px;
}

/* Progress Bar Container */
.progress-container {
  width: 200px;
}

.progress-container-enhanced {
  min-width: 250px;
}

/* Status Badge Styling */
.status-badge {
  font-size: 0.85rem;
  padding: 0.4rem 0.8rem;
  font-weight: 600;
}

/* Live Log Message */
.live-log-message {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-style: italic;
}

/* Estimated Time */
.estimated-time {
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

/* Inline Log Container */
.inline-log-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 0.85rem;
}

.log-entry-inline {
  padding: 0.4rem 0;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: flex-start;
}

.log-entry-inline:last-child {
  border-bottom: none;
}

/* Log Details Card */
.log-details-card {
  margin: 0.5rem 0;
  border-left: 3px solid #007bff;
}

/* Log Modal Styling */
.log-container-wrapper {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #2d3748;
}

.log-container-header {
  background-color: #2d3748;
  color: #e2e8f0;
  padding: 0.75rem 1.25rem;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: bold;
  border-bottom: 1px solid #1a202c;
  display: flex;
  align-items: center;
}

.log-container {
  background-color: #1a202c;
  color: #cbd5e0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  padding: 1.25rem;
  max-height: 400px;
  overflow-y: auto;
}

.log-entry {
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
}

.log-entry:last-child {
  border-bottom: none;
}

.log-timestamp {
  color: #718096;
  white-space: nowrap;
}

.log-message {
  word-break: break-word;
}

.log-info {
  color: #9ae6b4;
}

.log-error {
  color: #feb2b2;
}

.log-warning {
  color: #fbd38d;
}

/* Disabled Cursor */
.disabled-cursor {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Per Page Selector */
.per-page-selector {
  width: 90px;
}

/* Responsive Adjustments */
@media (max-width: 576px) {
  .pull-logs-container {
    padding: 1rem;
  }

  .progress-container {
    width: 150px;
  }

  .b-table {
    font-size: 12px;
  }
}

/* Make top-center toasts appear exactly in the middle of the screen */
::v-deep .b-toaster-top-center {
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 9999;
}

/* Enhanced Pull Summary styling */
.pull-summary-wrapper {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid #edf2f7;
}

.summary-stat-box {
  background: #f8fafc;
  border-radius: 10px;
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.summary-stat-box:hover {
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  border-color: #e2e8f0;
}

.summary-stat-box.bg-light-subtle {
  padding: 0.75rem 1.25rem;
  background: #fdfdfd;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 700;
}

.summary-value {
  display: flex;
  align-items: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

/* Modal Title Polish */
::v-deep .modal-header {
  border-bottom: 1px solid #f1f5f9;
  background-color: #f8fafc;
}

::v-deep .modal-title {
  font-weight: 700;
  color: #1e293b;
}
</style>
